/**
 * Build a natural-language query string from a structured SearchState.
 *
 * Used by SmartSearchCard when the user toggles Classic → AI so the AI
 * input box shows the equivalent of what they entered structurally (e.g.
 * "Round trip SFO → LIS, May 20 to May 27, 2 adults economy"). Inverse of
 * `parseAIQuery` — best-effort only, since the backend parser is the
 * authoritative direction.
 *
 * Returns an empty string when there's nothing meaningful to express;
 * callers can use that to decide whether to leave the AI input empty
 * (and the animated placeholder visible) or pre-fill.
 */

import { format, isValid, parseISO } from 'date-fns'
import type { SearchState } from '@/lib/search/types'

export function stateToNaturalQuery(state: SearchState): string {
  // If we already have a query string, that's authoritative — the user
  // last typed it. The Classic form may have parsed it into structured
  // fields, but the original phrasing is still the better source.
  if (state.query.trim().length > 0) return state.query

  const parts: string[] = []

  // Trip-type prefix
  if (state.tripType === 'one-way') parts.push('One way')
  else if (state.tripType === 'multi-city') parts.push('Multi-city')
  else parts.push('Round trip')

  // Origin → destination
  const route = formatRoute(state)
  if (route) parts.push(route)

  // Dates
  const dates = formatDates(state)
  if (dates) parts.push(dates)

  // Travelers
  const pax = formatPaxShort(state)
  if (pax) parts.push(pax)

  // Cabin (omit when economy — that's the default)
  if (state.cabin !== 'economy') parts.push(formatCabinShort(state.cabin))

  // Final assembly: first two parts joined by space, remainder by comma.
  // Example: "Round trip SFO → LIS, May 20 to May 27, 2 adults economy"
  if (parts.length <= 1) return ''
  const [head, ...rest] = parts
  const route2 = rest[0]
  const tail = rest.slice(1)
  return tail.length > 0 ? `${head} ${route2}, ${tail.join(', ')}` : `${head} ${route2}`
}

function formatRoute(state: SearchState): string | undefined {
  const from = state.from?.code
  const to = state.to?.code
  if (from && to) return `${from} → ${to}`
  if (to) return `to ${to}`
  if (from) return `from ${from}`
  return undefined
}

function formatDates(state: SearchState): string | undefined {
  const depart = parseDate(state.departureDate)
  const ret = parseDate(state.returnDate)
  if (depart && ret) return `${format(depart, 'MMM d')} to ${format(ret, 'MMM d')}`
  if (depart) return format(depart, 'MMM d')
  return undefined
}

function parseDate(s: string | undefined): Date | undefined {
  if (!s) return undefined
  const d = parseISO(s)
  return isValid(d) ? d : undefined
}

function formatPaxShort(state: SearchState): string | undefined {
  const { adults, children } = state.passengers
  // Skip the default of 1 adult / 0 children — no need to clutter the
  // generated query with a value that's effectively "unspecified".
  if (adults === 1 && children === 0) return undefined
  const parts: string[] = []
  parts.push(`${adults} ${adults === 1 ? 'adult' : 'adults'}`)
  if (children > 0) parts.push(`${children} ${children === 1 ? 'child' : 'children'}`)
  return parts.join(' ')
}

function formatCabinShort(cabin: SearchState['cabin']): string {
  if (cabin === 'premium-economy') return 'premium economy'
  if (cabin === 'business') return 'business class'
  if (cabin === 'first') return 'first class'
  return cabin
}
